import { useCallback, useMemo, useState } from 'react';
import { Button, Grid, Input, Modal, Select, Typography } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ZonesDropdown from '../../../components/ZonesDropdown';
import {
	allPreworkRecommendationStatuses,
	allPreworkStatuses,
	Gender,
	VALID_ABHYASI_ID_REGEX,
} from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import {
	PreworkApplicationFilters,
	PreworkReviewAction,
} from '../../../shared/PreWorkServiceTypes';
import { useBatchId } from '../../../hooks/useBatchId';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import { FUNCTIONARY_HOME } from '../../../shared/Routes';
import useUserSession from '../../../hooks/useUserSession';
import RegionsDropdown from '../../../components/RegionsDropdown';
import { OptionData as RegionData } from '../DashboardPage/FilterDashboard';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProps {
	value: PreworkApplicationFilters;
	onFilterChange: (value: PreworkApplicationFilters) => void;
}

interface OptionData {
	value: string;
	label: string;
}

export const Filter = ({
	value: { applicantName, gender, applicantSrcmId, preworkRecommendation },
	onFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { navigateToRoute } = useBatchId();
	const lengthyLanguages = ['ta', 'ma', 'te', 'ka'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const [nameValue, setNameValue] = useState<string | undefined>(applicantName);
	const [genderValue, setGenderValue] = useState<string | undefined>(gender);
	const [
		preworkRecommendationStatusValue,
		setPreworkRecommendationStatusValue,
	] = useState<PreworkReviewAction[] | undefined>(preworkRecommendation);
	const [region, setRegion] = useState<RegionData | null>(null);
	const [zoneNames, setZoneNames] = useState<Array<string> | undefined>(
		undefined
	);
	const [selectedZones, setSelectedZones] = useState<OptionData[]>([]);
	const [applicantSrcmIdValue, setApplicantSrcmId] = useState<
		string | undefined
	>(applicantSrcmId);
	const [applicantSrcmIdError, setApplicantSrcmIdError] = useState('');
	const isApprover = selectedRole === UserRole.APPROVER;

	const mentorFilters = useMemo(() => {
		return {
			preworkStatuses: allPreworkStatuses,
		};
	}, []);

	const approverFilters = useMemo(() => {
		return {
			...mentorFilters,
			preworkRecommendation: allPreworkRecommendationStatuses,
		};
	}, [mentorFilters]);

	const genderDropdownOptions = useMemo(() => {
		return [
			{
				value: Gender.NOT_PROVIDED,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('bothGenders')}
					</p>
				),
			},
			{
				value: Gender.MALE,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('male')}
					</p>
				),
			},
			{
				value: Gender.FEMALE,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('female')}
					</p>
				),
			},
		];
	}, [t]);

	const preworkRecommendationStatusOptions = useMemo(() => {
		return [
			{
				value: PreworkReviewAction.PREWORK_APPROVE,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('approve')}
					</p>
				),
			},
			{
				value: PreworkReviewAction.PREWORK_REJECT,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('allowMoreTime')}
					</p>
				),
			},
			{
				value: PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('extendPrework')}
					</p>
				),
			},
			{
				value: PreworkReviewAction.PENDING,
				label: (
					<p className="font-sans text-sm text-secondary-gray h-fit py-0 my-0">
						{t('pending')}
					</p>
				),
			},
		];
	}, [t]);

	const onApplicantNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setNameValue(e.target.value);
		},
		[]
	);

	const handleZoneChange = useCallback((values: OptionData[]) => {
		setSelectedZones(values);
		setZoneNames(values.map((z) => z.label));
	}, []);

	const handleGenderChange = useCallback((value: string) => {
		setGenderValue(value);
	}, []);

	const handlePreworkRecommendationStatusChange = useCallback(
		(value: PreworkReviewAction[]) => {
			setPreworkRecommendationStatusValue(value);
		},
		[]
	);

	const onApplicantSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setApplicantSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setApplicantSrcmIdError('invalidAbhyasiId');
			} else {
				setApplicantSrcmIdError('');
			}
		},
		[]
	);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const handleBackClick = useCallback(() => {
		navigateToRoute(FUNCTIONARY_HOME);
	}, [navigateToRoute]);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	const handleRegionChange = useCallback((value: RegionData) => {
		setRegion(value);
		setSelectedZones([]);
		setZoneNames([]);
	}, []);

	const handleResetFilters = useCallback(() => {
		setGenderValue('NOT_PROVIDED');
		setNameValue('');
		setRegion(null);
		setZoneNames([]);
		setSelectedZones([]);
		setApplicantSrcmId('');
		setApplicantSrcmIdError('');
		setPreworkRecommendationStatusValue(allPreworkRecommendationStatuses);

		if (isApprover) {
			onFilterChange(approverFilters);
		} else {
			onFilterChange(mentorFilters);
		}

		setIsFilterPopup(false);
		setIsFilterTextApplied(false);
	}, [approverFilters, isApprover, mentorFilters, onFilterChange]);

	const handleFilterButtonClick = useCallback(() => {
		onFilterChange({
			applicantSrcmId: applicantSrcmIdValue,
			applicantName: nameValue,
			gender: genderValue,
			preworkRecommendation: preworkRecommendationStatusValue,
			zoneNames,
			preworkStatuses: allPreworkStatuses,
		});
		setIsFilterPopup(false);
		setIsFilterTextApplied(true);
	}, [
		onFilterChange,
		applicantSrcmIdValue,
		nameValue,
		genderValue,
		preworkRecommendationStatusValue,
		zoneNames,
	]);

	return (
		<>
			<div className="flex justify-end sm:w-auto">
				<div className="flex gap-2 justify-between w-full">
					{isApprover && (
						<Button
							type="primary"
							onClick={handleBackClick}
							className="hidden md:block font-bold text-sm"
						>
							<span className="flex gap-2">
								<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
								{t('back')}
							</span>
						</Button>
					)}
					<div className="block sm:hidden">
						<a
							onClick={handleBackClick}
							className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
						>
							<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
						</a>
					</div>
					<Button
						className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid ${
							isFilterTextApplied ? 'border-primary-blue' : 'border-gray-200'
						} ${isMobile ? 'w-full' : ''}`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterTextApplied ? t('filterApplied') : t('filter')}
					</Button>
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
				</div>

				{isFilterTextApplied ? (
					<Button
						className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
						type="link"
						onClick={handleResetFilters}
					>
						<span>
							{t('resetFilters')}
							<UndoOutlined className="ml-2" />
						</span>
					</Button>
				) : (
					''
				)}
			</div>
			<Modal
				open={isFilterPopup}
				onCancel={onClose}
				footer={null}
				centered
				className={
					isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
				}
				closable={true}
				maskClosable={true}
			>
				<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
					{t('filter')}
				</div>

				<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="block text-xs text-gray-650">
								{t('filterByRegion')}
							</Text>
							<RegionsDropdown
								functionarySrcmId={srcmId}
								value={region}
								onChange={handleRegionChange}
							/>
						</div>
					</div>
					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="block text-xs text-gray-650">
								{t('filterByZone')}
							</Text>
							<ZonesDropdown
								functionarySrcmId={srcmId}
								value={selectedZones}
								onChange={handleZoneChange}
								multiple={true}
								regionId={region?.value}
							/>
						</div>
					</div>

					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="text-xs text-gray-650">
								{t('filterByGender')}
							</Text>
							<Select
								className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
								value={genderValue ?? 'NOT_PROVIDED'}
								options={genderDropdownOptions}
								onChange={handleGenderChange}
							/>
						</div>
					</div>
					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="text-xs text-gray-650">
								{t('filterByApplicantName')}
							</Text>
							<Input
								className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
								value={nameValue}
								allowClear
								onChange={onApplicantNameChange}
							/>
						</div>
					</div>
					<div className="w-full sm:w-[calc(50%-0.5rem)]">
						<div className="mb-[16px] flex flex-col gap-2">
							<Text className="text-xs text-gray-650">
								{t('filterByApplicantSRCMId')}
							</Text>
							<Input
								className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
								value={applicantSrcmIdValue}
								allowClear
								onChange={onApplicantSRCMIdChange}
							/>
							{applicantSrcmIdError && (
								<Text className="text-xs text-red-500">
									{t(applicantSrcmIdError)}
								</Text>
							)}
						</div>
					</div>
					{isApprover && (
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="text-xs text-gray-650">
									{t('filterByRecommendationStatus')}
								</Text>
								<Select
									mode="multiple"
									className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
									value={preworkRecommendationStatusValue}
									options={preworkRecommendationStatusOptions}
									onChange={handlePreworkRecommendationStatusChange}
								/>
							</div>
						</div>
					)}
					<div
						className={`flex ${
							isStackVertical
								? 'flex-col items-center space-y-2'
								: 'justify-center gap-2'
						} mt-4 ant-modal-footer`}
					>
						<Button
							className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
						<Button
							className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
								isStackVertical ? 'p-2' : ''
							}`}
							onClick={onClose}
						>
							{t('closeFilter')}
						</Button>
						<Button
							type="primary"
							onClick={handleFilterButtonClick}
							className={`flex-1 h-9 font-sans font-bold ${
								isStackVertical ? 'p-2 mt-3' : ''
							}`}
							disabled={applicantSrcmIdError !== ''}
						>
							{t('applyFilter')}
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};
